// Wizard stepper — one step model, two layouts.
//
//   horizontal (default): done / current / todo nodes in a centred row with
//     connector bars — the Programs creation wizard's top stepper.
//   rail: a vertical list with optional group headers and small dot markers —
//     ContentIQ's left-hand "map of the project". The page keeps the column
//     wrapper (width, border, footer hint); this renders only the list.
//
// Both consume the exact same `steps` array, so a page decides its layout
// with one prop and never re-implements state/click handling.
// Purely presentational; reusable by any future wizard (work-order/spin-up).
//
// Props:
//   steps: [{ key, label, state: 'done'|'current'|'todo',
//             group?: string,                 — rail only: header text, rendered
//                                               once each time it changes
//             badge?: { label, tone: 'gate'|'done'|'muted' }, clickable?: bool }]
//   onStepClick?: (key) => void   — fired only for steps marked clickable
//   orientation?: 'horizontal' | 'rail'   (default 'horizontal')
//   theme?: { accent, connector, stepWidth, connectorWidth,
//             railDone: { bg, border, text } }
//       Omit for the ContentIQ look; the Programs creation wizard passes NeTrA's
//       navy so it reads as core NeTrA. stepWidth/connectorWidth exist because the
//       default 168px + 56px only fits about five steps — a seven-step wizard
//       silently clipped its first and last nodes, since the row is centred. The
//       row also scrolls rather than clipping when it still doesn't fit.
//       railDone is the rail's "finished" palette — semantic green by default,
//       deliberately distinct from the horizontal layout's accent-filled node.
import React from 'react';
import { BRAND, HUES, INK, NEUTRAL, RADII, SHADOW, STATE } from '../utils/wizardTheme';

const DEFAULT_THEME = {
  accent: BRAND.button, connector: HUES.strategy.dim,
  stepWidth: '168px', connectorWidth: '56px',
  railDone: { bg: STATE.done.tint, border: STATE.done.border, text: STATE.done.text },
};

const nodeStyles = (accent) => ({
  done: {
    width: '34px', height: '34px', borderRadius: RADII.node, background: accent,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  current: {
    width: '34px', height: '34px', borderRadius: RADII.node, background: '#ffffff',
    border: `2px solid ${accent}`, boxSizing: 'border-box',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '14px', fontWeight: 700, color: accent, boxShadow: SHADOW.focus,
  },
  todo: {
    width: '34px', height: '34px', borderRadius: RADII.node, background: NEUTRAL.chipBg,
    border: `2px solid ${NEUTRAL.controlSoft}`, boxSizing: 'border-box',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '14px', fontWeight: 700, color: INK.muted,
  },
});

const labelColors = (accent) => ({ done: INK.secondary, current: accent, todo: INK.muted });
const BADGE_TONES = {
  gate: { color: STATE.gate.main, background: STATE.gate.tint },
  done: { color: STATE.done.main, background: STATE.done.tint },
  // Neutral, for informational badges like "skipped" — amber would read as
  // a problem, and a deliberately skipped step isn't one (2026-10-08).
  muted: { color: INK.muted, background: NEUTRAL.band },
};

function Check() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#ffffff"
      strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function Badge({ badge, style }) {
  return (
    <span style={{
      ...BADGE_TONES[badge.tone || 'gate'], borderRadius: RADII.pill,
      padding: '1px 9px', fontSize: '10px', fontWeight: 700, whiteSpace: 'nowrap',
      ...style,
    }}>
      {badge.label}
    </span>
  );
}

function RailStepper({ steps, onStepClick, t }) {
  const dot = (state) => ({
    width: '14px', height: '14px', borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
    background: state === 'done' ? t.railDone.bg : NEUTRAL.pageBg,
    border: state === 'done' ? `1px solid ${t.railDone.border}`
      : state === 'current' ? `4px solid ${t.accent}` : `1.5px solid ${NEUTRAL.controlSoft}`,
  });
  const rowColor = { done: t.railDone.text, current: INK.primary, todo: INK.muted };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', fontSize: '13px' }}>
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          {s.group && (i === 0 || steps[i - 1].group !== s.group) && (
            <div style={{
              fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: INK.muted,
              marginTop: i === 0 ? 0 : '10px',
            }}>
              {s.group}
            </div>
          )}
          <div
            onClick={() => s.clickable && onStepClick && onStepClick(s.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              cursor: s.clickable ? 'pointer' : 'default',
              color: rowColor[s.state],
              fontWeight: s.state === 'current' ? 700 : 400,
            }}>
            <span style={dot(s.state)} />
            {s.label}
            {s.badge && <Badge badge={s.badge} style={{ marginLeft: '2px' }} />}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export default function WizardStepper({ steps, onStepClick, theme, orientation = 'horizontal' }) {
  const t = { ...DEFAULT_THEME, ...(theme || {}) };
  if (orientation === 'rail') return <RailStepper steps={steps} onStepClick={onStepClick} t={t} />;

  const NODE = nodeStyles(t.accent);
  const LABEL_COLOR = labelColors(t.accent);
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', padding: '10px 0 4px',
      // Scroll rather than clip: the row is centred, so anything too wide loses
      // BOTH ends — including step 1, which is the one a user most needs to see.
      overflowX: 'auto',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0 }}>
        {steps.map((s, i) => (
          <React.Fragment key={s.key}>
            {i > 0 && (
              <div style={{
                height: '2.5px', width: t.connectorWidth, flexShrink: 0,
                marginTop: '16px', borderRadius: '2px',
                background: steps[i - 1].state === 'done' ? t.connector : NEUTRAL.controlSoft,
              }} />
            )}
            <div
              onClick={() => s.clickable && onStepClick && onStepClick(s.key)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: '7px', width: t.stepWidth, flexShrink: 0,
                textAlign: 'center', cursor: s.clickable ? 'pointer' : 'default',
              }}>
              <div style={NODE[s.state]}>
                {s.state === 'done' ? <Check /> : (i + 1)}
              </div>
              <div style={{
                fontSize: '12px', fontWeight: s.state === 'current' ? 700 : 600,
                color: LABEL_COLOR[s.state],
              }}>
                {s.label}
              </div>
              {s.badge && <Badge badge={s.badge} />}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
